<template>
	<div class="landing" ref="landing-home-page">
		<LazyGlobalHero :country="country" :language="language" />

		<span class="header-background" />

		<LazyGlobalCards :country="country" :language="language" />

		<!-- wrapper keeps .landing's child list stable through hydration -->
		<div class="landing__sr">
			<slot v-if="isMounted" name="sponsoredRecommendation" />
		</div>

		<div class="landing__heading">
			<h2>{{ $t('WEBAPP_GLOBAL_TITLES_HEADING') }}</h2>
		</div>

		<LazyGlobalTitles
			:country="country"
			:language="language"
			:objectType="ObjectType.Movie"
			type="TOP_10"
			module-name="MAIN_TOP_10_MOVIES"
			:titles="globalTitles?.MAIN_TOP_10_MOVIES?.edges ?? []"
		/>

		<section class="landing dynamic">
			<LazyGlobalTitles
				type="TOP_10"
				:objectType="ObjectType.Show"
				:language="language"
				:country="country"
				module-name="MAIN_TOP_10_SHOWS"
				:titles="globalTitles?.MAIN_TOP_10_SHOWS?.edges ?? []"
			/>

			<LazyGlobalTitles
				:country="country"
				:language="language"
				type="TOP_10"
				module-name="MAIN_JWTV_TVOD"
				:titles="globalTitles?.MAIN_JWTV_TVOD?.edges ?? []"
			/>

			<LazyGlobalLargeSpotlight
				:language="language"
				:country="country"
				module-name="MAIN_LARGE_SPOTLIGHT"
				:titles="globalTitles?.MAIN_LARGE_SPOTLIGHT?.edges ?? []"
			/>

			<LazyGlobalTitles
				:country="country"
				:language="language"
				type="POPULAR"
				module-name="MAIN_JWTV_AVOD"
				:titles="globalTitles?.MAIN_JWTV_AVOD?.edges ?? []"
			/>

			<LazyGlobalTitles
				type="NEW"
				:objectType="ObjectType.Movie"
				:language="language"
				:country="country"
				module-name="MAIN_NEW_MOVIES"
				:titles="globalTitles?.MAIN_NEW_MOVIES?.edges ?? []"
			/>

			<LazyGlobalTitles
				type="TOP_10"
				genre="cmy"
				:language="language"
				:country="country"
				module-name="MAIN_TOP_10_GENRE_COMEDY"
				:titles="globalTitles?.MAIN_TOP_10_GENRE_COMEDY?.edges ?? []"
			/>

			<LazyGlobalTitles
				type="TOP_10"
				genre="drm"
				:language="language"
				:country="country"
				module-name="MAIN_TOP_10_GENRE_DRAMA"
				:titles="globalTitles?.MAIN_TOP_10_GENRE_DRAMA?.edges ?? []"
			/>

			<LazyGlobalTitles
				type="TOP_10"
				genre="hrr"
				:language="language"
				:country="country"
				module-name="MAIN_TOP_10_GENRE_HORROR"
				:titles="globalTitles?.MAIN_TOP_10_GENRE_HORROR?.edges ?? []"
			/>

			<div class="landing__heading--sub">
				<h2>{{ $t('WEBAPP_GLOBAL_NEW_SHOW') }}</h2>
			</div>

			<LazyGlobalSmallSpotlight
				:language="language"
				:country="country"
				module-name="MAIN_SMALL_SPOTLIGHT"
				:titles="globalTitles?.MAIN_SMALL_SPOTLIGHT?.edges ?? []"
			/>

			<LazyGlobalTitles
				type="NEW"
				:country="country"
				:objectType="ObjectType.Show"
				:language="language"
				module-name="MAIN_NEW_SHOWS"
				:titles="globalTitles?.MAIN_NEW_SHOWS?.edges ?? []"
			/>

			<LazyGlobalProviderSpotlight
				v-if="!!providersByShortName[spotlightProvider.shortName]"
				:country="country"
				:language="language"
				:provider="spotlightProvider"
				module-name="MAIN_TOP_PROVIDER"
				:titles="globalTitles?.MAIN_TOP_PROVIDER?.edges ?? []"
			/>

			<LazyGlobalTitles
				type="POPULAR"
				:objectType="ObjectType.Movie"
				:language="language"
				:country="country"
				module-name="MAIN_POPULAR_MOVIES"
				:titles="globalTitles?.MAIN_POPULAR_MOVIES?.edges ?? []"
			/>

			<LazyGlobalTitles
				type="POPULAR"
				:objectType="ObjectType.Show"
				:language="language"
				:country="country"
				module-name="MAIN_POPULAR_SHOWS"
				:titles="globalTitles?.MAIN_POPULAR_SHOWS?.edges ?? []"
			/>

			<div v-if="hasSports" class="landing__heading">
				<h2>{{ $t('WEBAPP_GLOBAL_SPORTS_HEADING') }}</h2>
			</div>

			<!--
			Components that have children fetching queries like this one can't wait for hydration
			To-Do: aggregate queries and correctly leverage Vue renderer cache and apollo cache
		-->
			<GlobalSports
				v-if="hasSports"
				:language="language"
				:country="country"
				:webLocale="webLocale"
				module-name="MAIN_TOP_SPORTS"
				:sportCompetitions="globalTitles?.sportCompetitionsV2"
				:sportEventBuckets="globalTitles?.sportEventBucketsV2"
			/>

			<div class="recommendations">
				<div class="jw-logo" style="--aspect-ratio: 1">
					<img :src="logoSrc" loading="lazy" alt="JustWatch LOGO" />
				</div>

				<h2 class="recommendations__text">
					{{ $t('WEBAPP_GLOBAL_BEST_MOVIES_SHOWS') }}
				</h2>

				<CallToAction @click="$emit('scrollToTop')" />
			</div>
		</section>

		<div class="container countries-list">
			<h2>{{ $t('WEBAPP_GLOBALHOME_COUNTRIES_TITLE') }}</h2>
			<LazyCountriesList />
		</div>

		<!-- Sub components of AppFooter NuxtLazyHydrate -->
		<AppFooter class="footer-container" />
	</div>
</template>

<script setup lang="ts">
const LazyGlobalCards = defineLazyHydrationComponent(
	'idle',
	() => import('@/features/global-home/components/card/GlobalCards.vue')
);
const LazyGlobalHero = defineLazyHydrationComponent(
	'idle',
	() => import('@/features/global-home/components/GlobalHero.vue')
);
const LazyGlobalLargeSpotlight = defineLazyHydrationComponent(
	'visible',
	() => import('@/features/global-home/components/GlobalLargeSpotlight.vue')
);
const LazyGlobalProviderSpotlight = defineLazyHydrationComponent(
	'visible',
	() => import('@/features/global-home/components/GlobalProviderSpotlight.vue')
);
const LazyGlobalSmallSpotlight = defineLazyHydrationComponent(
	'visible',
	() => import('@/features/global-home/components/GlobalSmallSpotlight.vue')
);
const LazyGlobalTitles = defineLazyHydrationComponent(
	'visible',
	() => import('@/features/global-home/components/GlobalTitles.vue')
);
const LazyCountriesList = defineLazyHydrationComponent(
	'idle',
	() => import('@/components/countries-list/CountriesList.vue')
);

import { ObjectType, Platform } from '@/@types/graphql-types';
import { COUNTRY_EXCEPTIONS } from '@/constants/web-locales.constant';
import CallToAction from '@/features/global-home/components/CallToAction.vue';
import {
	GetGlobalHomeDocument,
	GetGlobalHomeQuery,
	GetGlobalHomeQueryVariables,
} from '@/features/global-home/components/graphql/queries/GetGlobalHome.query';
import { isSportSupported } from '@/features/sports/types/enums';
import { HomepageSports } from '@/features/sports/types/sports-types';

import { usePersistedTodaysDate } from '@/helpers/composables/usePersistedTodaysDate';

import { useConstantStore } from '@/stores/pinia/useConstantStore';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';

import { storeToRefs } from 'pinia';
import { computed, toValue } from 'vue';
import { useMounted } from '@vueuse/core';
import { captureMessageForSentry } from '@/libraries/sentry';

const GlobalSports = defineAsyncComponent(() => import('@/features/global-home/components/sports/GlobalSports.vue'));

// Hardcoded value for now @liam.best
const spotlightProvider = { shortName: 'atp' };

const logoSrc = `/${ASSETS_DIR}/img/home/logo.svg`;

const { providersByShortName } = storeToRefs(useConstantStore());
const { country, language, webLocale } = storeToRefs(useLanguageStore());
const { todayDate } = usePersistedTodaysDate();

const isMounted = useMounted();

const globalTitlesQuery = useQuery<GetGlobalHomeQuery, GetGlobalHomeQueryVariables>(
	GetGlobalHomeDocument,
	() => {
		return {
			platform: Platform.Web,
			language: toValue(language),
			country: toValue(country),
			dateToday: todayDate.value.toISOString().split('T')[0],
			timezoneOffset: 0,
			competitionFilter: { activeOn: todayDate.value.toISOString() },
			eventFilter: {
				startAt: { from: todayDate.value.toISOString(), to: null },
				competitionIds: [],
				sports: HomepageSports,
			},
			sportBucketSize: 2,
		};
	},
	{ errorPolicy: 'all', fetchPolicy: 'cache-first' }
);

const globalTitles = computed(() => globalTitlesQuery.result.value);
globalTitlesQuery.onError(error => {
	captureMessageForSentry('[GraphQL Get Global Home]:', { error: error, where: 'Component: GlobalHome' }, 'error');
});

const hasSports = computed(() => {
	const isException = COUNTRY_EXCEPTIONS[webLocale.value];
	if (isException) {
		return isSportSupported(isException);
	}

	return isSportSupported(webLocale.value);
});
</script>

<style lang="scss" scoped>
@import '~@/styles/variables.scss';

.landing {
	display: flex;
	flex-direction: column;
	align-items: center;
	width: 100%;
	background: $jw-color-black;

	@media (max-width: 1050px) {
		transform: translateY(-105px);
	}

	// `.landing` centers its children, so without this the SR collapses to fit-content
	&__sr {
		width: 100%;
	}

	// stays in the DOM when there is no SR so hydration keeps its place
	&__sr:empty {
		display: none;
	}

	&__heading {
		width: 85%;
		margin-bottom: -60px;
		margin-top: 60px !important;

		@media (max-width: 550px) {
			width: 90%;
			margin-bottom: -64px;
			margin-top: 30px !important;
		}

		@media (max-width: 1000px) and (min-width: 550px) {
			margin-bottom: 0px;
		}

		> h2 {
			font-size: 45px;
			font-weight: 900;
			width: 60%;
			text-align: start;
			line-height: 50px;
			margin: 0;

			@media (max-width: 550px) {
				font-size: 28px;
				line-height: 36px;
				margin: 0 0 30px 0;
			}

			@media (max-width: 1000px) {
				width: 100%;
			}
		}

		&--sub {
			width: 85%;
			margin-bottom: -100px;

			@media (max-width: 1000px) {
				width: 90%;
				margin-bottom: -40px;
			}

			> h2 {
				font-size: 32px;
				font-weight: 900;
				margin-top: 0;
				margin-bottom: 0;
				margin-inline-end: 20px;
				text-align: start;
				color: #eaebee;

				@media (max-width: 550px) {
					font-size: 20px;
				}
			}
		}
	}

	> * {
		margin-top: 125px;

		@media (max-width: 1000px) {
			margin-top: 60px;
		}
	}

	.dynamic {
		margin-top: 0;
	}
}

.recommendations {
	display: flex;
	flex-direction: column;
	align-items: center;
	text-align: center;
	padding: 0 14px;
	max-width: 50em;

	.jw-logo {
		width: 68px;
		height: 68px;
		margin-bottom: 0.6em;

		> img {
			width: 68px;
			height: 68px;
		}
	}

	&__text {
		font-size: 35px;
	}
}

.countries-list {
	h2 {
		text-align: center;
	}
}

.footer-container {
	margin-top: -6em;
}

.header-background {
	position: -webkit-sticky;
	position: sticky;
	top: 0;
	margin-bottom: -100px;
	width: 100%;
	height: 86px;
	background-color: black;
	z-index: 99;
	margin: 0;
	margin-bottom: -100px;

	@media (max-width: 1050px) {
		height: 105px;
	}
}
</style>
